import { ApolloError, gql, useLazyQuery, useQuery } from '@apollo/client';
import { MAYA_GRAPHQL_ENDPOINT } from 'basics/constants/common.constants';
import { Products, DropMaterialsFilter } from 'generated/maya.types';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

const GET_CRM_DROP_MATERIALS = gql`
  query getCRMDropMaterials($filters: DropMaterialsFilter) {
    getCRMDropMaterials(filters: $filters) {
      productID
      productName
      commercialFunctionFlag
      marketAccessFunctionFlag
      medicalFunctionFlag
    }
  }
`;

type ResultType = {
  getCRMDropMaterials: Products[];
};

type UseGetCRMDropMaterials = (filters: DropMaterialsFilter, skip?: boolean) => {
  loading: boolean;
  error?: ApolloError;
  data: Products[];
};

type UseGetCRMDropMaterialsLazy = () => {
  getCRMDropMaterialsLazy: Function;
  loading: boolean;
  error?: ApolloError;
  data: Products[];
};

const useGetCRMDropMaterials: UseGetCRMDropMaterials = (filters: DropMaterialsFilter, skip = false) => {
  const { data, loading, error } = useQuery<ResultType>(GET_CRM_DROP_MATERIALS, {
    fetchPolicy: 'cache-and-network',
    context: {
      clientName: MAYA_GRAPHQL_ENDPOINT,
    },
    skip,
    variables: {
      filters,
    },
  });

  if (error) {
    const customContext = extractApolloErrorDetails(error);
    Logger.logError({
      error,
      customContext,
      transactionName: 'getCRMDropMaterials.ts - useGetCRMDropMaterials',
    });
  }

  return {
    data: data?.getCRMDropMaterials || [],
    loading,
    error,
  };
};

export const useGetCRMDropMaterialsLazy: UseGetCRMDropMaterialsLazy = () => {
  const [getCRMDropMaterialsLazy, { data, loading, error }] = useLazyQuery<ResultType>(GET_CRM_DROP_MATERIALS, {
    fetchPolicy: 'network-only',
    context: {
      clientName: MAYA_GRAPHQL_ENDPOINT,
    },
  });

  if (error) {
    const customContext = extractApolloErrorDetails(error);
    Logger.logError({
      error,
      customContext,
      transactionName: 'getCRMDropMaterials.ts - useGetCRMDropMaterialsLazy',
    });
  }

  return {
    getCRMDropMaterialsLazy,
    data: data?.getCRMDropMaterials || [],
    loading,
    error,
  };
};

export default useGetCRMDropMaterials;
